/**
 * Call queue class
 */

import { AstNilString } from './helper'
import QueueAgent from './queue_agent'

const FIND_BY_MEMBERNAME = Symbol('find agent agenet by extention using membername ext@domain')

export default class Queue {
  constructor (data) {
    this.id = `${data.name}_${data.realmid}`
    this.dbId = data.id
    this.name = data.name
    this.calls = []
    this.realmid = data.realmid
    this.holdtime = ~~data.summary?.holdtime
    this.completed = ~~data.summary?.completed
    this.abandoned = ~~data.summary?.abandoned
    this.watch = data.summary ? data.summary.watch : false

    this.agents = []
    if (Array.isArray(data.summary?.agents)) {
      data.summary.agents.forEach((a) => {
        this.agents.push(new QueueAgent(a))
      })
    }
  }

  /* @static */
  static find (queues, qid) {
    return queues.find((q) => q.id === qid)
  }

  static totalActiveCalls (queues) {
    return queues.reduce((n, q) =>
      n + q.agents.reduce((i, a) => i + a.calls.length, 0), 0)
  }

  static totalCallWaiting (queues) {
    return queues.reduce((n, q) => n + q.calls.length, 0)
  }

  static totalCompletedCalls (queues) {
    return queues.reduce((n, q) => n + q.completed, 0)
  }

  static totalAbandonedCalls (queues) {
    return queues.reduce((n, q) => n + q.abandoned, 0)
  }

  static totalAgents (queues) {
    return queues.reduce((n, q) => n + q.agents.length, 0)
  }

  static totalAgentsPaused (queues) {
    return queues.reduce((n, q) =>
      n + q.agents.reduce((i, a) => i + a.paused, 0), 0)
  }

  /**
   * Extract call from AMI event CoreShowChannel.
   * @throws Error when fails
   */
  static callFromQueueChan (queues, data) {
    const queueName = data.applicationdata
      ? data.applicationdata.split(',').shift()
      : null
    if (!queueName) {
      throw new Error('No queue name in application data')
    }
    const queue = Queue.find(queues, queueName)
    if (!queue) {
      throw new Error(`No queue match name '${queueName}' from application data`)
    }
    if (!data.chanvariable?.BRIDGEPEER) {
      throw new Error('No BRIDGEPEER channel to match')
    }
    const match = data.chanvariable.BRIDGEPEER
      .match('^Local/([0-9]+)-[0-9]+@from-queue.*$')
    if (!match) {
      throw new Error('Failed to get agent from BRIDGEPEER')
    }
    const agentExten = match[1]
    const agent = queue.agents.find(a => a.exten === agentExten)
    if (!agent) {
      throw new Error(`No Agent with exten '${agentExten}' in queue '${queue.id}'`)
    }
    agent.addCall(data)
  }

  /* @public */
  callerJoin (evt) {
    const call = this.calls.find((c) => c.channel === evt.channel)
    if (call) return // already in queue
    this.calls.push({
      channel: evt.channel,
      state: evt.channelstate,
      callerNum: evt.calleridnum,
      callerName: evt.calleridname,
      position: ~~evt.position,
      wait: 0,
      start: Date.now(),
      isCallback: evt.channel.startsWith('callback/')
    })
  }

  agentCalled (evt) {
    const agent = this[FIND_BY_MEMBERNAME](evt.membername)
    if (agent) {
      agent.ringing = true
    }
  }

  agentConnect (evt) {
    const agent = this[FIND_BY_MEMBERNAME](evt.membername)
    if (!agent) return
    agent.addCall(evt)
  }

  memberStatus (evt) {
    const agent = this[FIND_BY_MEMBERNAME](evt.membername)
    if (!agent) return

    agent.penalty = ~~evt.penalty
    agent.answered = ~~evt.callstaken
    agent.lastcall = ~~evt.lastcall
    agent.status = ~~evt.status
    agent.ringing = ~~evt.status === 6
  }

  callerLeave (data) {
    const idx = this.calls.findIndex((c) => c.channel === data.channel)
    if (idx === -1) return // not found
    this.calls.splice(idx, 1)
  }

  agentComplete (evt) {
    const agent = this[FIND_BY_MEMBERNAME](evt.membername)
    if (!agent) return

    agent.delCall(evt.channel)

    agent.holdtime = ~~evt.holdtime
    agent.calltime = ~~evt.talktime
  }

  callerAbandon (evt) {
    this.abandoned++
    this.holdtime += ~~evt.holdtime
  }

  memberPause (evt) {
    const agent = this[FIND_BY_MEMBERNAME](evt.membername)
    if (!agent) return

    agent.paused = ~~evt.paused === 1
    agent.status = ~~evt.status
    agent.lastcall = ~~evt.lastcall
    agent.answered = ~~evt.callstaken
    agent.pausedReason = AstNilString(evt.pausedreason)
  }

  queueParams (evt) {
    this.holdtime = ~~evt.holdtime
    this.completed = ~~evt.completed
    this.abandoned = ~~evt.abandoned
  }

  queueMember (evt) {
    const agent = this[FIND_BY_MEMBERNAME](evt.name)
    if (!agent) return
    agent.penalty = ~~evt.penalty
    agent.answered = ~~evt.callstaken
    agent.lastcall = ~~evt.lastcall
    agent.status = ~~evt.status
    agent.paused = ~~evt.paused === 1
    agent.pausedReason = AstNilString(evt.pausedreason)
  }

  queueEntry (evt) {
    const call = this.calls.find((c) => c.channel === evt.channel)
    if (call) {
      call.position = ~~evt.position
      call.wait = ~~evt.wait
    } else {
      this.calls.push({
        channel: evt.channel,
        state: '',
        callerNum: evt.calleridnum,
        callerName: evt.calleridname,
        position: ~~evt.position,
        wait: ~~evt.wait,
        start: Date.now() - (evt.wait * 1000),
        isCallback: evt.channel.startsWith('callback/')
      })
    }
  }

  [FIND_BY_MEMBERNAME] (membername) {
    const ext = membername.split('@')[0]
    if (!ext) return null
    return this.agents.find((a) => a.exten === ext)
  }
}
